export function getTreatmentName(treatment = {}) {
  return String(
    treatment?.name ||
      treatment?.treatmentName ||
      treatment?.factors?.treatmentName ||
      treatment?.factors?.name ||
      ""
  ).toLowerCase();
}

export function getTreatmentFactors(treatment = {}) {
  return treatment?.factors || {};
}

function getTreatmentSetting(treatment = {}, key) {
  return treatment?.[key] ?? getTreatmentFactors(treatment)?.[key];
}

function isBaseRateStaticSyntheticGroupTreatmentName(treatment = {}) {
  const treatmentName = getTreatmentName(treatment);
  return (
    treatmentName.includes("story-baserate-socratic-nopolish-syngroup") ||
    treatmentName.includes("story-baserate-template-nopolish-syngroup") ||
    treatmentName.includes("story-baserate-socratic-groupfirst-syngroup") ||
    treatmentName.includes("story-baserate-template-groupfirst-syngroup") ||
    treatmentName.includes("story-baserate-socratic-nopolish-extremepeer-syngroup") ||
    treatmentName.includes("story-baserate-template-nopolish-extremepeer-syngroup") ||
    treatmentName.includes("story-baserate-socratic-polish-syngroup") ||
    treatmentName.includes("story-baserate-template-polish-syngroup")
  );
}

export function getTreatmentTask(treatment = {}) {
  return treatment?.task || getTreatmentFactors(treatment)?.task || "";
}

export function getTreatmentType(treatment = {}) {
  return treatment?.treatmentType || getTreatmentFactors(treatment)?.treatmentType || "";
}

export function getTreatmentPlayerCount(treatment = {}) {
  return Number(
    treatment?.playerCount || getTreatmentFactors(treatment)?.playerCount || 0
  );
}

function normalizeCurrencyAmount(value, fallback) {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
}

function formatCurrencyAmount(amount) {
  return `$${Number.isInteger(amount) ? amount : amount.toFixed(1).replace(/\.0$/, "")}`;
}

function normalizePositiveInteger(value, fallback) {
  const parsed = Number(value);
  return Number.isInteger(parsed) && parsed > 0 ? parsed : fallback;
}

export function getCompensationConfig(treatment = {}) {
  const useBaseRateStaticSyntheticGroupOverride =
    isBaseRateStaticSyntheticGroupTreatmentName(treatment);
  const basePaymentAmount = normalizeCurrencyAmount(
    useBaseRateStaticSyntheticGroupOverride
      ? 2
      : getTreatmentSetting(treatment, "basePaymentAmount"),
    3
  );
  const accuracyBonusAmount = normalizeCurrencyAmount(
    useBaseRateStaticSyntheticGroupOverride
      ? 1.5
      : getTreatmentSetting(treatment, "accuracyBonusAmount"),
    3.5
  );
  const mostConvincingBonusAmount = normalizeCurrencyAmount(
    useBaseRateStaticSyntheticGroupOverride
      ? 0
      : getTreatmentSetting(treatment, "mostConvincingBonusAmount"),
    1.5
  );
  const accuracyBonusBasis =
    String(getTreatmentSetting(treatment, "accuracyBonusBasis") || "").trim() ||
    (useBaseRateStaticSyntheticGroupOverride
      ? "individual-final-estimate"
      : "group-majority");
  const hasMostConvincingBonus = useBaseRateStaticSyntheticGroupOverride
    ? false
    : getTreatmentSetting(treatment, "hasMostConvincingBonus") !== false;
  const hasHelpfulnessRating = useBaseRateStaticSyntheticGroupOverride
    ? false
    : getTreatmentSetting(treatment, "hasHelpfulnessRating") === true;
  const basePaymentAmountText = formatCurrencyAmount(basePaymentAmount);
  const basePaymentText =
    String(
      (
        useBaseRateStaticSyntheticGroupOverride
          ? "$2 ($9/hour)"
          : getTreatmentSetting(treatment, "basePaymentDisplay")
      ) || ""
    ).trim() ||
    `${basePaymentAmountText}${
      basePaymentAmount === 2
        ? " ($9/hour)"
        : basePaymentAmount === 3
          ? " (roughly $9/hour)"
          : ""
    }`;

  return {
    basePaymentAmount,
    basePaymentAmountText,
    basePaymentText,
    accuracyBonusAmount,
    accuracyBonusText: formatCurrencyAmount(accuracyBonusAmount),
    accuracyBonusBasis,
    mostConvincingBonusAmount,
    mostConvincingBonusText: formatCurrencyAmount(
      mostConvincingBonusAmount
    ),
    hasMostConvincingBonus,
    hasHelpfulnessRating,
  };
}

export function getFakeLobbyConfig(treatment = {}) {
  const useBaseRateStaticSyntheticGroupOverride =
    isBaseRateStaticSyntheticGroupTreatmentName(treatment);
  if (useBaseRateStaticSyntheticGroupOverride) {
    return {
      enabled: false,
      minSeconds: 3,
      maxSeconds: 10,
    };
  }
  return {
    enabled: getTreatmentSetting(treatment, "fakeLobbyEnabled") === true,
    minSeconds: normalizePositiveInteger(
      getTreatmentSetting(treatment, "fakeLobbyMinSeconds"),
      3
    ),
    maxSeconds: normalizePositiveInteger(
      getTreatmentSetting(treatment, "fakeLobbyMaxSeconds"),
      10
    ),
  };
}

export function getSyntheticGroupMode(treatment = {}) {
  return String(
    treatment?.syntheticGroupMode ||
      getTreatmentFactors(treatment)?.syntheticGroupMode ||
      ""
  ).toLowerCase();
}

export function getPreLobbyMode(treatment = {}) {
  const factors = getTreatmentFactors(treatment);
  const explicitMode = treatment?.preLobbyMode || factors?.preLobbyMode;
  if (explicitMode) {
    return String(explicitMode).toLowerCase();
  }

  const treatmentName = getTreatmentName(treatment);
  if (
    treatmentName.includes("story-retire-pilot-nogroup-template") ||
    treatmentName.includes("story-tax-pilot-nogroup-template") ||
    treatmentName.includes("story-tax-math-nogroup-template") ||
    treatmentName.includes("story-tax-math-openend-nogroup-template") ||
    treatmentName.includes("story-baserate-nogroup-template")
  ) {
    return "template";
  }

  if (
    treatmentName.includes("story-tax-math-socratic-nogroup-norationale") ||
    treatmentName.includes("story-tax-math-openend-socratic-nogroup-norationale") ||
    treatmentName.includes("story-baserate-socratic-nogroup-norationale")
  ) {
    return "socratic";
  }

  return "";
}

// Length and framing of the rationale collected on the initial-prediction
// screen. Two values are in use. "brief" is a short pre-treatment box: because
// it is collected before the reflection activity and is identical in all three
// arms, it cannot attenuate or confound the coaching contrast the way a
// post-treatment write-up does. "none" collects no participant-written
// rationale anywhere in the arm. Absent on every legacy treatment, whose
// initial screens are unchanged.
export function getInitialRationaleMode(treatment = {}) {
  const factors = getTreatmentFactors(treatment);
  const explicitMode =
    treatment?.initialRationaleMode || factors?.initialRationaleMode;
  return String(explicitMode || "").toLowerCase();
}

export function usesBriefInitialRationale(treatment = {}) {
  return getInitialRationaleMode(treatment) === "brief";
}

// Both modes suppress the 50-word post-treatment write-up, and they differ only
// in whether the initial box renders. The two predicates are therefore separate.
// Dropping the factor instead of setting it to "none" would restore the write-up
// in the template and Socratic arms, and preLobbyMode "none" ends the pre-lobby
// at the initial screen, so the baseline arm could never render it. The arms
// would stop matching on the one step this design holds fixed.
export function suppressesPostConfirmWriteup(treatment = {}) {
  const mode = getInitialRationaleMode(treatment);
  return mode === "brief" || mode === "none";
}

// Where the reflection activity runs relative to the peer-review stage.
// "pre-peer" is the default for every condition that does not set the factor.
export function getReflectionTiming(treatment = {}) {
  const factors = getTreatmentFactors(treatment);
  const explicitTiming = treatment?.reflectionTiming || factors?.reflectionTiming;
  return String(explicitTiming || "").toLowerCase() === "post-peer"
    ? "post-peer"
    : "pre-peer";
}

// Arms that hold the condition-specific instructions back until after the
// initial estimate and confidence are recorded, so that everything preceding
// those two quantities is identical across the three preparation conditions.
// "pre-initial" is the default and is the order every arm fielded before
// 2026-09-11 runs.
//
// The factor is the declared mechanism and is what a new arm should set. The
// name list backs it up because the factor map a participant sees is the one
// frozen into the batch when the batch was created, not the one in
// server/src/treatments.js at run time. A batch created before the factor
// shipped would otherwise run the old order with no error anywhere, and a
// silent revert to the order the pre-registration rules out is worth two lines
// of redundancy.
const postInitialConditionInstructionTreatmentNames = new Set([
  "story-baserate-none-assypeerassign",
  "story-baserate-template-assypeerassign",
  "story-baserate-socratic-assypeerassign",
  // The two follow-up designs (writeup/prereg_groupfirst_sep26.md,
  // writeup/prereg_askall_sep26.md) register the same order, with the peer
  // review between the instruction page and the activity.
  "story-baserate-template-groupfirst-followup",
  "story-baserate-socratic-groupfirst-followup",
  "story-baserate-template-askall-followup",
  "story-baserate-socratic-askall-followup",
]);

export function getConditionInstructionsTiming(treatment = {}) {
  const explicitTiming = getTreatmentSetting(
    treatment,
    "conditionInstructionsTiming"
  );
  if (String(explicitTiming || "").toLowerCase() === "post-initial") {
    return "post-initial";
  }
  return postInitialConditionInstructionTreatmentNames.has(
    getTreatmentName(treatment)
  )
    ? "post-initial"
    : "pre-initial";
}

export function usesPostInitialConditionInstructions(treatment = {}) {
  return getConditionInstructionsTiming(treatment) === "post-initial";
}

// Post-peer flags. Each is off unless the treatment sets it to true, so every
// arm fielded before the follow-up designs keeps its behavior.
//
// postPeerAskAll: the reflection activity addresses all three answers (own and
// both peers).
export function usesPostPeerAskAll(treatment = {}) {
  return getTreatmentSetting(treatment, "postPeerAskAll") === true;
}

// postPeerAskAllOpenEntry: the ask-all follow-up keeps the post-peer estimate
// stage, discusses that estimate as the participant's own answer, and takes the
// final answer as a typed number. Without it, the ask-all arms run the piloted
// procedure: no post-peer estimate, and a closed choice at the final answer.
export function usesPostPeerAskAllOpenEntry(treatment = {}) {
  return (
    usesPostPeerAskAll(treatment) &&
    getTreatmentSetting(treatment, "postPeerAskAllOpenEntry") === true
  );
}

// The piloted ask-all procedure, where the final answer is a choice among the
// participant's own estimate and the two peers' answers.
export function usesPostPeerClosedChoice(treatment = {}) {
  return usesPostPeerAskAll(treatment) && !usesPostPeerAskAllOpenEntry(treatment);
}

// postPeerNoRerate: the final-answer screen does not ask for a second
// convincingness rating of the two peers, so the rating is collected once, at
// the peer review, as in the main study.
export function usesPostPeerNoRerate(treatment = {}) {
  return getTreatmentSetting(treatment, "postPeerNoRerate") === true;
}

// postPeerChangedAnswerConfidence: a participant whose final answer differs
// from the post-peer estimate also reports the confidence still assigned to the
// post-peer estimate, with the two ratings summing to at most 100. This is the
// main study's third confidence, placed at the screen that follows the activity.
export function usesPostPeerChangedAnswerConfidence(treatment = {}) {
  return getTreatmentSetting(treatment, "postPeerChangedAnswerConfidence") === true;
}

// postPeerExitTwoStep: the exit survey asks why the participant kept or changed
// the estimate at both revision points (after the peer review and after the
// activity), and the activity items name the step that follows the peer review.
export function usesPostPeerExitTwoStep(treatment = {}) {
  return getTreatmentSetting(treatment, "postPeerExitTwoStep") === true;
}

// syntheticPairMode: asymmetric is set only by the *-assypeerassign arms. The exit
// survey uses it to show the two probability-background items in those arms only.
export function isAsymmetricPeerAssignment(treatment = {}) {
  return (
    String(getTreatmentSetting(treatment, "syntheticPairMode") || "").toLowerCase() ===
    "asymmetric"
  );
}

export function getPostConfirmWriteupMode(treatment = {}) {
  const factors = getTreatmentFactors(treatment);
  const explicitMode =
    treatment?.postConfirmWriteupMode || factors?.postConfirmWriteupMode;
  if (explicitMode) {
    return String(explicitMode).toLowerCase();
  }

  if (hasShareableGroupRationale(treatment)) {
    return "group-shareable";
  }

  return "";
}

export function getPostConfirmPolishMode(treatment = {}) {
  const factors = getTreatmentFactors(treatment);
  const explicitMode =
    treatment?.postConfirmPolishMode || factors?.postConfirmPolishMode;
  if (explicitMode) {
    return String(explicitMode).toLowerCase();
  }

  return "";
}

export function hasShareableGroupRationale(treatment = {}) {
  const factors = getTreatmentFactors(treatment);
  return treatment?.shareableRationale === true || factors?.shareableRationale === true;
}

export function isBaseRateGroup3Condition(treatment = {}) {
  return (
    getPostConfirmWriteupMode(treatment) === "group-shareable" &&
    getTreatmentTask(treatment) === "base-rate" &&
    getTreatmentType(treatment) === "Control" &&
    getTreatmentPlayerCount(treatment) === 3
  );
}

export function isBaseRateStaticSyntheticGroupCondition(treatment = {}) {
  return (
    getSyntheticGroupMode(treatment) === "static-rationales" &&
    getPostConfirmWriteupMode(treatment) === "group-shareable" &&
    getTreatmentTask(treatment) === "base-rate" &&
    getTreatmentType(treatment) === "Control" &&
    getTreatmentPlayerCount(treatment) === 1
  );
}

export function isFourTaskPilotCondition(treatment = {}) {
  const treatmentName = getTreatmentName(treatment);
  return (
    treatmentName.includes("pilot_4tasks_notreatment") ||
    treatment?.fourTaskPilot === true ||
    treatment?.factors?.fourTaskPilot === true
  );
}

export function isStoryNogroupCondition(treatment = {}) {
  if (hasShareableGroupRationale(treatment)) {
    return false;
  }

  const treatmentName = getTreatmentName(treatment);
  const task = getTreatmentTask(treatment);
  const treatmentType = getTreatmentType(treatment);

  return (
    treatmentName.includes("story-retire-pilot-nogroup-meta") ||
    treatmentName.includes("story-retire-pilot-nogroup-devil") ||
    treatmentName.includes("story-retire-pilot-nogroup-template") ||
    treatmentName.includes("story-tax-pilot-nogroup-meta") ||
    treatmentName.includes("story-tax-pilot-nogroup-devil") ||
    treatmentName.includes("story-tax-pilot-nogroup-template") ||
    treatmentName.includes("story-tax-math-socratic-nogroup-norationale") ||
    treatmentName.includes("story-tax-math-nogroup-template") ||
    treatmentName.includes("story-tax-math-openend-socratic-nogroup-norationale") ||
    treatmentName.includes("story-tax-math-openend-nogroup-template") ||
    treatmentName.includes("story-baserate-socratic-nogroup-norationale") ||
    treatmentName.includes("story-baserate-nogroup-template") ||
    ((task === "pension-reform" || task === "flat-tax" || task === "flat-tax-math" || task === "flat-tax-math-openend" || task === "base-rate") &&
      treatmentType === "nogroup")
  );
}

export function isStoryNogroupTemplateCondition(treatment = {}) {
  if (hasShareableGroupRationale(treatment)) {
    return false;
  }

  if (getTreatmentType(treatment) === "nogroup" && getPreLobbyMode(treatment) === "template") {
    return true;
  }

  const treatmentName = getTreatmentName(treatment);
  return (
    treatmentName.includes("story-retire-pilot-nogroup-template") ||
    treatmentName.includes("story-tax-pilot-nogroup-template") ||
    treatmentName.includes("story-tax-math-nogroup-template") ||
    treatmentName.includes("story-tax-math-openend-nogroup-template") ||
    treatmentName.includes("story-baserate-nogroup-template")
  );
}

export function isPasteEnabledNogroupCondition(treatment = {}) {
  const factors = getTreatmentFactors(treatment);
  if (treatment?.pasteEnabled === true || factors?.pasteEnabled === true) {
    return true;
  }

  const treatmentName = getTreatmentName(treatment);
  return (
    treatmentName.includes("story-tax-math-socratic-nogroup-norationale") ||
    treatmentName.includes("story-tax-math-nogroup-template") ||
    treatmentName.includes("story-tax-math-openend-socratic-nogroup-norationale") ||
    treatmentName.includes("story-tax-math-openend-nogroup-template") ||
    treatmentName.includes("story-baserate-socratic-nogroup-norationale") ||
    treatmentName.includes("story-baserate-nogroup-template")
  );
}
